import type { ErrorCopyFor } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";
import type { HARNESS_LOGIN_ERROR_CODES, VAULT_ERROR_CODES } from "@amtana/vault-contract";

const EN = {
  notFoundTitle: "This secret no longer exists",
  notFoundDescription: "Someone may already have deleted it. Reload the list to see what remains.",
  nameTakenTitle: "That name is taken",
  nameTakenDescription: "Another secret already uses this name or the same placeholder. Choose another name.",
  unknownAgentTitle: "An agent is no longer here",
  unknownAgentDescription: "One of the agents you picked was deleted. Reload and pick again.",
  loginRefusedTitle: "That sign-in was not accepted",
  loginRefusedDescription:
    "The code may be from another sign-in or already used, or the request was declined. Start again.",
  loginExpiredTitle: "This sign-in expired",
  loginExpiredDescription: "A sign-in lasts 15 minutes. Start again to get a new code.",
  loginMissingTitle: "Not signed in yet",
  loginMissingDescription:
    "Sign in with Claude or Codex on the agent's page. Until you do, its tasks are refused.",
  loginUnreachableTitle: "The sign-in did not answer",
  loginUnreachableDescription: "Claude or OpenAI did not answer. Try again in a moment.",
} as const;

const ES: Translation<typeof EN> = {
  notFoundTitle: "Este secreto ya no existe",
  notFoundDescription: "Puede que alguien ya lo haya eliminado. Recarga la lista para ver cuáles quedan.",
  nameTakenTitle: "Ese nombre ya está en uso",
  nameTakenDescription: "Otro secreto ya usa este nombre o el mismo marcador. Elige otro nombre.",
  unknownAgentTitle: "Un agente ya no está",
  unknownAgentDescription: "Se eliminó uno de los agentes que elegiste. Recarga y vuelve a elegir.",
  loginRefusedTitle: "No se aceptó ese inicio de sesión",
  loginRefusedDescription:
    "Puede que el código sea de otro inicio de sesión o ya se haya usado, o que se rechazara la solicitud. Empieza de nuevo.",
  loginExpiredTitle: "Este inicio de sesión caducó",
  loginExpiredDescription:
    "Un inicio de sesión dura 15 minutos. Empieza de nuevo para obtener un código nuevo.",
  loginMissingTitle: "Aún no has iniciado sesión",
  loginMissingDescription:
    "Inicia sesión con Claude o Codex en la página del agente. Mientras tanto, sus tareas se rechazan.",
  loginUnreachableTitle: "El inicio de sesión no respondió",
  loginUnreachableDescription: "Claude u OpenAI no respondieron. Inténtalo de nuevo en un momento.",
};

export const VAULT_ERROR_CATALOGS: Catalogs<typeof EN> = { en: EN, es: ES };

export const VAULT_ERROR_COPY = {
  vault_secret_not_found: {
    title: phrase(VAULT_ERROR_CATALOGS, "notFoundTitle"),
    description: phrase(VAULT_ERROR_CATALOGS, "notFoundDescription"),
  },
  vault_name_taken: {
    title: phrase(VAULT_ERROR_CATALOGS, "nameTakenTitle"),
    description: phrase(VAULT_ERROR_CATALOGS, "nameTakenDescription"),
  },
  vault_unknown_agent: {
    title: phrase(VAULT_ERROR_CATALOGS, "unknownAgentTitle"),
    description: phrase(VAULT_ERROR_CATALOGS, "unknownAgentDescription"),
  },
} satisfies ErrorCopyFor<typeof VAULT_ERROR_CODES>;

/** The member's own Claude or Codex sign-in (ADR-023 §7): what the agent's page says for each. */
export const HARNESS_LOGIN_ERROR_COPY = {
  harness_login_refused: {
    title: phrase(VAULT_ERROR_CATALOGS, "loginRefusedTitle"),
    description: phrase(VAULT_ERROR_CATALOGS, "loginRefusedDescription"),
  },
  harness_login_expired: {
    title: phrase(VAULT_ERROR_CATALOGS, "loginExpiredTitle"),
    description: phrase(VAULT_ERROR_CATALOGS, "loginExpiredDescription"),
  },
  harness_login_missing: {
    title: phrase(VAULT_ERROR_CATALOGS, "loginMissingTitle"),
    description: phrase(VAULT_ERROR_CATALOGS, "loginMissingDescription"),
  },
  harness_login_unreachable: {
    title: phrase(VAULT_ERROR_CATALOGS, "loginUnreachableTitle"),
    description: phrase(VAULT_ERROR_CATALOGS, "loginUnreachableDescription"),
  },
} satisfies ErrorCopyFor<typeof HARNESS_LOGIN_ERROR_CODES>;
